import {
  TProgressiveTier,
  getDisplayableTiers,
  formatShippingTierLabel,
  formatTierQuantityLabel,
  isAmountTrigger
} from '@cannect/services/resources/progressive-discounts'
import { formatReal } from '@cannect/utils/formatters'
import { useState } from 'react'

type Props = {
  tiers: TProgressiveTier[]
  onSelectTier?: (quantity: number) => void
  /** Preço real do produto na PDP (usado para calcular unitFinal por faixa). */
  productPrice?: number
  /** Tipo de gatilho da regra: 'quantity' (unidades) ou 'amount' (R$). */
  triggerType?: string
  /** Escopo da condição da regra: 'targeted' (produtos da marca) ou 'cart' (carrinho todo). */
  conditionScope?: string
}

export const ProgressiveDiscountTiers = ({ tiers, onSelectTier, productPrice, triggerType, conditionScope }: Props) => {
  const displayableTiers = getDisplayableTiers(tiers)
  const [selectedMinQty, setSelectedMinQty] = useState<number | null>(null)
  const isAmount = isAmountTrigger(triggerType)
  // Nota de marca só aparece quando a regra é por valor e escopo é 'targeted'
  const showBrandNote = isAmount && conditionScope === 'targeted'

  if (!displayableTiers.length) return null

  const handleTierClick = (minQty: number) => {
    setSelectedMinQty(minQty)
    onSelectTier?.(minQty)
  }

  // Faixas amount nunca são clicáveis: clicar colocaria R$ de threshold como unidades no carrinho
  const isClickable = !!onSelectTier && !isAmount

  return (
    <div className="mt-4 w-full rounded-2xl border border-primary-100 bg-primary-50/40 p-4">
      {!isAmount && onSelectTier && (
        <p className="mb-2 text-xs text-neutral-500">Clique em uma faixa para selecionar a quantidade.</p>
      )}

      <div className="flex flex-col gap-2">
        {displayableTiers.map((tier) => {
          const isSelected = !isAmount && selectedMinQty === tier.min_quantity
          const pct = Number(tier.discount_percent) || 0
          const hasItemBenefit = pct > 0
          const hasShippingBenefit = Number(tier.shipping_value) > 0
          // Usa o preço real do produto (não o unit_final_price da API, que é baseado em catálogo)
          const unitFinal = productPrice && hasItemBenefit ? +(productPrice * (1 - pct / 100)).toFixed(2) : null
          // Multiplicar preço por threshold em R$ não tem sentido — exibe total só para quantity
          const totalFinal = unitFinal && !isAmount ? +(unitFinal * tier.min_quantity).toFixed(2) : null

          const Wrapper = isClickable ? 'button' : 'div'
          const wrapperProps = isClickable
            ? {
                type: 'button' as const,
                onClick: () => handleTierClick(tier.min_quantity)
              }
            : {}

          return (
            <Wrapper
              key={tier.min_quantity}
              {...wrapperProps}
              className={[
                'flex w-full items-center justify-between rounded-xl border px-4 py-2.5 text-left transition-colors',
                isClickable ? 'cursor-pointer' : '',
                isSelected ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-400' : 'border-primary-100 bg-white',
                // Hover so na faixa clicavel: em regra por valor a faixa e informativa e o realce
                // convidaria a um clique que nao existe.
                isClickable && !isSelected ? 'hover:border-primary-300 hover:bg-primary-50/60' : ''
              ]
                .filter(Boolean)
                .join(' ')}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-neutral-600">
                  {formatTierQuantityLabel(tier, triggerType)}
                  {showBrandNote && '*'}
                </span>
                {hasItemBenefit && (
                  <span className="rounded-full bg-primary-600 px-2 py-0.5 text-xs font-semibold text-white">
                    {tier.discount_percent}% OFF
                  </span>
                )}
                {hasShippingBenefit && (
                  <span className="rounded-full bg-primary-600 px-2 py-0.5 text-xs font-semibold text-white">
                    {formatShippingTierLabel(tier)}
                  </span>
                )}
              </div>
              {unitFinal !== null && (
                <div className="text-right">
                  <p className="text-sm font-semibold text-neutral-800">
                    {(formatReal(unitFinal) as string) || `R$ ${unitFinal}`}
                    <span className="text-xs font-normal text-neutral-500"> /un</span>
                  </p>
                  {totalFinal !== null && (
                    <p className="text-xs text-neutral-500">
                      Total: {(formatReal(totalFinal) as string) || `R$ ${totalFinal}`}
                    </p>
                  )}
                </div>
              )}
            </Wrapper>
          )
        })}
      </div>
      {showBrandNote && (
        <p className="mt-2 text-xs text-neutral-500">*Condição válida apenas para produtos da marca.</p>
      )}
    </div>
  )
}
